import { Button, DynamicContainer, Form, FormContext, FormSection } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import React, { Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { triggerEvent } from 'src/js/events';
import { useStores } from 'src/js/hook/use-stores';
import streams, { addStream, fetchStreamStatistics, updateStream } from 'src/js/pages/streaming/stream/stream-store';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/util/constant';
import { changes } from 'src/js/util/formik-helper';
import { submitAndValidate } from 'src/js/validator/validator';

import audioEncoders from '../audio-encoder/audio-encoder-store';
import { metadataName, metadataNameOrConstructed } from '../metadata/metadata-models';
import metadataModels from '../metadata/metadata-store';
import scteModels from '../scte/scte-store';
import videoEncoders from '../video-encoder/video-encoder-store';
import { StreamEditAccessControlForm, StreamEditAccessControlFormRef } from './stream-edit-access-control';
import {
  FecRTP,
  isEmptyContentSource,
  MetadataEncapsulation,
  MpegProFecLevel,
  NetworkInterfaces,
  SRTAuthentication,
  SRTAuthenticationListener,
  SRTAuthenticationOthers,
  SRTEncryption,
  SRTMode,
  SrtPathRedundancyMode,
  SrtPathRedundancyModeOffered,
  SrtRedundancyPathRole,
  StreamEncapsulation,
  StreamEncapsulationWithoutSRT,
  StreamShaping,
  StreamViewModel,
} from './stream-models';
import { streamValidationSchema } from './stream-validations';

interface Props {
  model: StreamViewModel;
  onCancel?: VoidFunction;
}

export interface StreamAddFormRef {
  submitStream: () => Promise<void>;
}

export const StreamEdit = forwardRef((props: Props, ref: Ref<StreamAddFormRef>) => {
  const { model, onCancel } = props;
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const formRef = useRef<FormikProps<StreamViewModel>>();
  const accessControlFormRef = useRef<StreamEditAccessControlFormRef>();
  const nameField = useRef<HTMLInputElement>();
  const newStream = model.id === -1;
  const maxStreamContentSources = systemInfo.maxStreamContentSources;

  // setting a state here prevents live update from modifying the edit form
  const [settings, setSettings] = useState<StreamViewModel>(model);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [showTsFields, setShowTsFields] = useState(false);

  useEffect(() => {
    if (!newStream) {
      fetchStreamStatistics(model, t, setSettings); // refetch individual is required to load sap parameters
    } else {
      nameField.current?.focus?.();
    }
  }, []);

  useWatchObject(streams, (_updates) => {
    // compare if any changes from upstream since form was opened (settings vs _updates) and notify
  });

  const handleSubmit = (values: any) => {
    setButtonState('pending');

    if (newStream) {
      return addStream(formRef.current, formRef.current.values, t).then((res) => {
        setButtonState(undefined);
        if (res.ok) {
          triggerEvent('preset-update');
        }
        return res;
      });
    } else {
      return updateStream(values, t).then((res: any) => {
        if (res.ok) {
          const stream = streams.find((e) => e.id === model.id);
          setSettings(stream); // update edit view with newest model
          formRef.current.resetForm({ values: stream });
          triggerEvent('preset-update');
        }
        setButtonState(undefined);
        return res;
      });
    }
  };

  const checkTsFields = (formContext: FormikProps<StreamViewModel>) => {
    // Open up TS Settings if an error occured in sub-form
    if (
      Object.keys(formContext.errors).some((v) =>
        [
          'videoPids',
          'audioPids',
          'metadataPids',
          'sctePids',
          'pcrPid',
          'pmtPid',
          'tsStreamId',
          'programNumber',
        ].includes(v),
      )
    ) {
      setShowTsFields(true);
    }
  };

  const submitStream = () => {
    return submitAndValidate(formRef.current, t).then((res) => {
      if (!formRef.current?.isValid) {
        checkTsFields(formRef.current);
      }
      return res;
    });
  };

  useImperativeHandle(ref, () => ({
    submitStream,
  }));

  const onChangeSrtMode = (e: any) => {
    if (e.target.value === SRTMode.RENDEZVOUS) {
      formRef.current.setFieldValue('sourcePort', formRef.current.values.port);
    }
    if (accessControlFormRef?.current) {
      accessControlFormRef.current.onChangeSrtMode?.();
    }
  };

  const onChangeVideo = (e: any) => {
    if (
      formRef.current.initialValues?.video[0]?.id === e.target.value ||
      (formRef.current.initialValues?.video?.length === 0 && e.target.value === -1)
    ) {
      formRef.current.setFieldValue('video', formRef.current.initialValues?.video);
    } else {
      formRef.current.setFieldValue('video', [{ id: e.target.value }]);
    }
  };

  const onChangeScte = (e: any) => {
    if (
      formRef.current.initialValues?.scte[0]?.id === e.target.value ||
      (formRef.current.initialValues?.scte?.length === 0 && e.target.value === -1)
    ) {
      formRef.current.setFieldValue('scte', formRef.current.initialValues?.scte);
    } else {
      formRef.current.setFieldValue('scte', [{ id: e.target.value }]);
    }
  };

  const onChangePort = (e: any) => {
    if (formRef.current.values.srtMode === SRTMode.RENDEZVOUS) {
      formRef.current.setFieldValue('sourcePort', e.target.value);
    }
  };

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit}
      className="hai-mt-n8"
      restValidationProps={{
        enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
        validationSchema: streamValidationSchema(t),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(formContext: FormikProps<StreamViewModel>) => {
          const values = formContext.values;
          let showRedundancy = false;
          let showRedundancyPrimaryPathRows = false;
          const audioCount = values.audio?.length;
          const videoCount = values.video?.length;
          const metadataCount = values.metadata?.length;
          const scteCount = values.scte?.length;
          const totalSourceCount = audioCount + videoCount + metadataCount + scteCount;
          const disableAddAll = totalSourceCount >= maxStreamContentSources ? true : false;
          const disableAudioAdd = audioCount === 0 || (audioCount === 1 && values.audio[0].id === -1); // if 'None' selected
          const disableMetadataAdd = metadataCount === 0 || (metadataCount === 1 && values.metadata[0].id === -1); // if 'None' selected

          if (values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER) {
            if (values.srtRedundancyMode === SrtPathRedundancyMode.NONE) {
              showRedundancy = false;
            } else {
              showRedundancy = true;
              if (values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE) {
                showRedundancyPrimaryPathRows = false;
              } else {
                showRedundancyPrimaryPathRows = true;
              }
            }
          }
          let showNonRedundantCallerRendezvous = false;
          if (values.encapsulation === StreamEncapsulation.SRT && showRedundancy === false) {
            if (values.srtMode === SRTMode.CALLER || values.srtMode === SRTMode.RENDEZVOUS) {
              showNonRedundantCallerRendezvous = true;
            }
          }
          const isListener = values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER;

          let isAnyMetadata = false;
          if (systemInfoCapabilities.hasMetadata() && values.metadata && values.metadata.length > 0) {
            values.metadata.forEach((item) => {
              if (item.id !== -1 && item.id >= 0) {
                isAnyMetadata = true;
              }
            });
          }

          const fixSrtFields = (
            srtMode: SRTMode,
            srtRedundancyMode: SrtPathRedundancyMode,
            authentication: SRTAuthentication,
          ) => {
            if (srtMode == SRTMode.LISTENER) {
              if (authentication === undefined || authentication === SRTAuthentication.NONE) {
                formContext.setFieldValue('authentication', SRTAuthenticationListener.AUTO);
              }
              if (srtRedundancyMode === undefined || srtRedundancyMode === SrtPathRedundancyMode.NONE) {
                formContext.setFieldValue('srtRedundancyMode', SrtPathRedundancyMode.OPTIONAL);
              }
            } else {
              // non-listener
              if (authentication === undefined || authentication === SRTAuthentication.AUTO) {
                formContext.setFieldValue('authentication', SRTAuthenticationOthers.NONE);
              }
              if (srtRedundancyMode === undefined || srtRedundancyMode === SrtPathRedundancyMode.OPTIONAL) {
                formContext.setFieldValue('srtRedundancyMode', SrtPathRedundancyMode.NONE);
              }
            }
          };

          let applyButtonEnabled = formContext.dirty;
          const changed = changes(values, formContext.initialValues);
          delete (changed as any).audio_multi;
          delete (changed as any).metadata_multi;

          if (changed.metadata !== undefined) {
            if (isEmptyContentSource(formContext.initialValues.metadata) && isEmptyContentSource(values.metadata)) {
              // both are empty written in different ways
              //console.log('both content source are empty', formContext.initialValues.metadata, values.metadata);
              delete changed.metadata;
            }
          }
          if (changed.audio !== undefined) {
            if (isEmptyContentSource(formContext.initialValues.audio) && isEmptyContentSource(values.audio)) {
              // both are empty written in different ways
              //console.log('both content source are empty', formContext.initialValues.audio, values.audio);
              delete changed.audio;
            }
          }

          if (changed.metadataEncapsulation) {
            if (!isAnyMetadata) {
              // field is hidden
              delete changed.metadataEncapsulation;
            }
          }

          if (changed.sap) {
            if (values.sap.advertise === false) {
              // field is hidden
              delete changed.sap;
            }
          }

          if (Object.keys(changed).length === 0) {
            applyButtonEnabled = false;
          }

          return (
            <>
              <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
                <FormTextfield
                  inputRef={nameField}
                  name="name"
                  mask
                  placeholder={values.name}
                  title={t('general.name')}
                  className="max-width"
                  autoFocus={newStream}
                />
                {!newStream && !sessionStore.isUser() && (
                  <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                    <Button className="hai-mr-6" onClick={onCancel}>
                      {t('general.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => submitStream()}
                      disabled={!applyButtonEnabled}
                      state={buttonState}
                    >
                      {t('general.apply')}
                    </Button>
                  </div>
                )}
              </DynamicContainer>

              <FormSection className="hai-mb-6" title={t('stream.sections.source')} key={t('stream.sections.source')}>
                <FormBuilder
                  translationPrefixLabels="stream.fields"
                  translationPrefixEnums="stream.enums"
                  layout={FormLayout.THREE_COLUMNS_GRID}
                  fields={[
                    {
                      type: FormFieldType.COLUMN_START,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'video[]',
                      label: t('stream.fields.video'),
                      options: [{ label: t('general.noneSelected'), value: -1 }].concat(
                        videoEncoders.map((v) => ({ label: v.name, value: v.id })),
                      ),
                      value: videoCount > 0 ? values.video[0].id : -1,
                      onChange: onChangeVideo,
                    },
                    {
                      type: FormFieldType.COLUMN_END,
                    },

                    {
                      type: FormFieldType.MULTI_DROPDOWN,
                      name: 'audio',
                      options:
                        audioCount <= 1
                          ? [{ label: t('general.noneSelected'), value: -1 }].concat(
                              audioEncoders.map((a) => ({ label: a.name, value: a.id })),
                            )
                          : audioEncoders.map((a) => ({ label: a.name, value: a.id })),
                      value: values.audio.map((a) => a.id),
                      setValues: (newValues: any) => {
                        formContext.setFieldValue(
                          'audio',
                          newValues.map((a: any, i: number) => ({ ...values.audio[i], id: a })),
                        );
                        newValues.forEach((v: any, index: Number) => {
                          formContext.setFieldValue('audio_multi[' + index + ']', v); // workaround for HAIUI dropdown which dont listen to defaultValue changes
                        });
                      },
                      maxAmount: disableAddAll || disableAudioAdd ? 0 : systemInfo.audioEncoders,
                    },
                    {
                      type: FormFieldType.COLUMN_START,
                    },
                    {
                      type: FormFieldType.MULTI_DROPDOWN,
                      name: 'metadata',
                      options:
                        metadataCount <= 1
                          ? [{ label: t('general.noneSelected'), value: -1 }].concat(
                              metadataModels.map((v) => ({
                                label: metadataNameOrConstructed(v, t),
                                value: v.id,
                              })),
                            )
                          : metadataModels.map((v) => ({ label: metadataName(v, t), value: v.id })),
                      value: values.metadata.map((m) => m.id),
                      setValues: (newValues: any) => {
                        formContext.setFieldValue(
                          'metadata',
                          newValues.map((m: any, i: number) => ({ ...values.metadata[i], id: m })),
                        );
                        newValues.forEach((m: any, index: Number) => {
                          formContext.setFieldValue('metadata_multi[' + index + ']', m); // workaround for HAIUI dropdown which dont listen to defaultValue changes
                        });
                      },
                      when: systemInfoCapabilities.hasMetadata(),
                      maxAmount: disableAddAll || disableMetadataAdd ? 0 : metadataModels.length,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'metadataEncapsulation',
                      enum: MetadataEncapsulation,
                      when: isAnyMetadata,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'scte[]',
                      label: t('stream.fields.scte'),
                      options: [{ label: t('general.noneSelected'), value: -1 }].concat(
                        scteModels.map((v) => ({ label: v.name, value: v.id })),
                      ),
                      value: scteCount > 0 ? values.scte[0].id : -1,
                      onChange: onChangeScte,
                      breakBefore: true,
                      when: systemInfo.scte,
                    },
                    {
                      type: FormFieldType.COLUMN_END,
                    },
                  ]}
                />
              </FormSection>

              <FormBuilder
                translationPrefixLabels="stream.fields"
                translationPrefixEnums="stream.enums"
                layout={FormLayout.THREE_COLUMNS_GRID}
                fields={[
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.parameters'),
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'encapsulation',
                    enum: systemInfoCapabilities.hasSRT() ? StreamEncapsulation : StreamEncapsulationWithoutSRT,
                    onChange: (e) => {
                      if (e.target.value === StreamEncapsulation.SRT && values.overheadPercentage == 15) {
                        formContext.setFieldValue('overheadPercentage', 25);
                      } else if (e.target.value !== StreamEncapsulation.SRT && values.overheadPercentage == 25) {
                        formContext.setFieldValue('overheadPercentage', 15);
                      }
                    },
                  },
                  {
                    type: FormFieldType.SWITCH,
                    label: t('stream.showTsFields'),
                    className: 'mk-inline-toggler',
                    value: showTsFields,
                    onChange: (checked: boolean) => {
                      setShowTsFields(checked);
                    },
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  { type: FormFieldType.SECTION_START, when: showTsFields },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'videoPids',
                    placeholder: values.videoPidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.videoPidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'audioPids',
                    placeholder: values.audioPidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.audioPidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'metadataPids',
                    placeholder: values.metadataPidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.metadataPidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'sctePids',
                    placeholder: values.sctePidsPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.sctePidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'pcrPid',
                    placeholder: values.pcrPidPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.pcrPidExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'pmtPid',
                    placeholder: values.pmtPidPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.pmtPidExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'tsStreamId',
                    hint: t('stream.fields.tsStreamIdExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'programNumber',
                    placeholder: values.programNumberPlaceholder ?? t('stream.pidAutoAssigned'),
                    hint: t('stream.fields.programNumberExplain'),
                  },
                  { type: FormFieldType.SECTION_END },
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.destination'),
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtMode',
                    enum: SRTMode,
                    when: values.encapsulation === StreamEncapsulation.SRT,
                    onChange: (value: any) => {
                      fixSrtFields(value.target.value, values.srtRedundancyMode, values.authentication);
                      onChangeSrtMode(value);
                    },
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'port',
                    mask: true,
                    hint: t('stream.fields.portExplain'),
                    when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER,
                    required: true,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtListenerSecondPort',
                    mask: true,
                    placeholder: t('stream.srtListenerSecondPortPlaceholder'),
                    when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'networkInterface',
                    label: t('stream.fields.networkInterface'),
                    enum: NetworkInterfaces,
                    when: values.encapsulation !== StreamEncapsulation.SRT && systemInfo.hasEth1,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtRedundancyMode',
                    options: Object.values(SrtPathRedundancyModeOffered).map((value) => ({
                      value: value,
                      label: t(`stream.srtRedundancyOptions.${value}`),
                    })),
                    value: values.srtRedundancyMode,
                    when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER,
                  },
                  {
                    type: FormFieldType.SUBTITLE,
                    label: t('stream.sections.path1'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtRedundancyPath1Role',
                    enum: SrtRedundancyPathRole,
                    when: showRedundancyPrimaryPathRows,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtRedundancyPath1Name',
                    hint: t('stream.fields.srtRedundancyPath1NamePlaceholder'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'networkInterface',
                    label: t('stream.fields.networkInterface'),
                    enum: NetworkInterfaces,
                    when: systemInfo.hasEth1 && values.encapsulation === StreamEncapsulation.SRT && !isListener,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'address',
                    mask: true,
                    when: showRedundancy || !isListener,
                    required: true,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: showRedundancy ? 'srtRedundancyPath1SourcePort' : 'sourcePort',
                    mask: true,
                    placeholder: t('stream.fields.srtRedundancyPath1SourcePortPlaceholder'),
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy || showNonRedundantCallerRendezvous,
                    disabled: values.srtMode === SRTMode.RENDEZVOUS,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'port',
                    label: t('stream.fields.srtRedundancyPath1Port'),
                    mask: true,
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy || showNonRedundantCallerRendezvous,
                    required: true,
                    onChange: onChangePort,
                  },
                  {
                    type: FormFieldType.SUBTITLE,
                    label: t('stream.sections.path2'),
                    when:
                      values.encapsulation === StreamEncapsulation.SRT &&
                      values.srtMode === SRTMode.CALLER &&
                      (values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
                        values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP),
                  },
                  {
                    type: FormFieldType.STATIC_TEXT,
                    name: 'pathRole',
                    label: t('stream.fields.pathRole'),
                    value:
                      values.srtRedundancyPath1Role === 'secondary'
                        ? t('stream.enums.srtRedundancyRoleOptions.primary')
                        : t('stream.enums.srtRedundancyRoleOptions.secondary'),
                    when: showRedundancyPrimaryPathRows,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtRedundancyPath2Name',
                    hint: t('stream.fields.srtRedundancyPath2NamePlaceholder'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtRedundancyPath2NetworkInterface',
                    label: t('stream.fields.networkInterface'),
                    enum: NetworkInterfaces,
                    when: showRedundancy && systemInfo.hasEth1,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtRedundancyPath2Address',
                    when: showRedundancy,
                    required: true,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtRedundancyPath2SourcePort',
                    placeholder: t('stream.fields.srtRedundancyPath2SourcePortPlaceholder'),
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtRedundancyPath2Port',
                    mask: true,
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy,
                    required: true,
                  } /* TODO: conditional line break that supports "when" prop
                  {
                    type: FormFieldType.LINE_BREAK,
                    when: showRedundancy,
                  },*/,
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'port',
                    mask: true,
                    hint:
                      values.encapsulation === StreamEncapsulation.RTP
                        ? t('stream.fields.portNumberRTPExplain')
                        : t('stream.fields.portExplain'),
                    required: true,
                    when:
                      values.encapsulation === StreamEncapsulation.UDP ||
                      values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.srtSettings'),
                    when: values.encapsulation === StreamEncapsulation.SRT,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'latency',
                    hint: t('stream.fields.latencyExplain'),
                    when: values.encapsulation === StreamEncapsulation.SRT,
                    required: true,
                  },
                  {
                    type: FormFieldType.CHECKBOX,
                    name: 'adaptive',
                    when: values.encapsulation === StreamEncapsulation.SRT,
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'encKeyLength',
                    enum: SRTEncryption,
                    when: values.encapsulation === StreamEncapsulation.SRT,
                    onChange: () => {
                      fixSrtFields(values.srtMode, values.srtRedundancyMode, values.authentication);
                    },
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'authentication',
                    enum: SRTAuthenticationListener,
                    value: values.authentication /* WORKAROUND HAIUI ISSUE */,
                    when:
                      values.encapsulation === StreamEncapsulation.SRT &&
                      values.encKeyLength > 0 &&
                      values.srtMode === SRTMode.LISTENER,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'authentication',
                    enum: SRTAuthenticationOthers,
                    value: values.authentication /* WORKAROUND HAIUI ISSUE */,
                    when:
                      values.encapsulation === StreamEncapsulation.SRT &&
                      values.encKeyLength > 0 &&
                      values.srtMode !== SRTMode.LISTENER,
                  },
                  {
                    type: FormFieldType.INPUT_PASSWORD,
                    name: 'passphrase',
                    mask: true,
                    id: 'passphrase',
                    pwdToggle: true,
                    autoComplete: 'new-password',
                    placeholder: values.encrypted ? constant.obfuscatedPassphrase : undefined,
                    when: values.encapsulation === StreamEncapsulation.SRT && values.encKeyLength > 0,
                    required: true,
                  },
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.srtAccessControl'),
                    when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER,
                  } /*
                  {
                    type: FormFieldType.SUBTITLE,
                    node: (<><FormSwitch
                    title={t('stream.form.srt.srtAccessControl')}
                    name={'srtStreamId'}
                    className="bold"
                    switchLabel={values.srtStreamId ? t('general.on') : t('general.off')}
                    onChange={() => {
                      if (
                        values.srtAccessControlPublishingIdType !== 'custom' &&
                        values.srtAccessControlPublishingIdType !== 'standard'
                      ) {
                        // This can happen on an existing non-SRT stream. The srtAccessControlPublishingIdType will not be returned by REST API so undefined.
                        values.srtAccessControlPublishingIdType = 'standard'; // default is standard
                      }
                    }}/></>),
                  },*/,
                  {
                    type: FormFieldType.NODE,
                    node: <StreamEditAccessControlForm ref={accessControlFormRef} {...formContext} />,
                  },
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.linkParameters'),
                  },
                  {
                    type: FormFieldType.STATIC_TEXT,
                    name: 'bandwithEstimate',
                    when: values.id !== -1,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'fecRtp',
                    enum: FecRTP,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.SECTION_START,
                    when: values.fecRtp === FecRTP.MPEG_PRO_FEC,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'level',
                    enum: MpegProFecLevel,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'columns',
                    hint:
                      values.level === MpegProFecLevel.COLUMNS_ONLY
                        ? t('stream.fields.columnsExplainA')
                        : t('stream.fields.columnsExplainB'),
                    required: true,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'rows',
                    hint: t('stream.fields.rowsExplain'),
                    required: values.level !== MpegProFecLevel.COLUMNS_ONLY,
                    disabled: values.level === MpegProFecLevel.COLUMNS_ONLY,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.CHECKBOX,
                    name: 'blockAligned',
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.SECTION_END,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'shaping',
                    enum: StreamShaping,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'metadataBandwidth',
                    placeholder: values.metadataBandwidthPlaceholder,
                    when: isAnyMetadata && values.shaping !== StreamShaping.VBR,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'overheadPercentage',
                    hint:
                      values.encapsulation === StreamEncapsulation.SRT
                        ? t('stream.fields.overheadPercentageSrtExplain')
                        : t('stream.fields.overheadPercentageExplain'),
                    required: true,
                    when: values.encapsulation === StreamEncapsulation.SRT || values.shaping !== StreamShaping.VBR,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'mtu',
                    hint: t('stream.fields.mtuExplain'),
                    required: true,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'tos',
                    hint: t('stream.fields.tosExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'ttl',
                    hint: t('stream.fields.ttlExplain'),
                    required: true,
                  },
                ]}
              />
              {values.encapsulation !== StreamEncapsulation.SRT && (
                <FormBuilder
                  key="stream-form-sap"
                  translationPrefixLabels="stream.fields"
                  translationPrefixEnums="stream.enums"
                  layout={FormLayout.THREE_COLUMNS_GRID}
                  fields={[
                    {
                      type: FormFieldType.TITLE,
                      label: t('stream.sections.sap'),
                      className: 'hai-mb-0',
                    },
                    {
                      type: FormFieldType.SWITCH,
                      name: 'sap.advertise',
                    },
                    {
                      type: FormFieldType.LINE_BREAK,
                    },
                    {
                      type: FormFieldType.SECTION_START,
                      when: values.sap.advertise,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'sap.name',
                      mask: true,
                      required: true,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'sap.desc',
                      mask: true,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'sap.author',
                      mask: true,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'sap.keywords',
                      mask: true,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'sap.copyright',
                      mask: true,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'sap.address',
                      mask: true,
                      value: values.sap.addressConfigured ?? '',
                      placeholder: values.sapAddressPlaceholder ?? t('general.autoAssign'),
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'sap.port',
                      mask: true,
                      placeholder: '9875',
                    },
                    {
                      type: FormFieldType.SECTION_END,
                    },
                  ]}
                />
              )}
            </>
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
});

StreamEdit.displayName = 'StreamEdit';
